// Bootstrap
import { Dropdown } from "bootstrap";
import { configureNativeViewport } from "@/services/nativeViewport";
import {
    captureLoginLinkCredentials,
    redactCredentialsDeep
} from "@/services/loginLinkCredentials";

// Must run before any analytics, error reporting or widget reads the URL.
captureLoginLinkCredentials();
configureNativeViewport();

// Dropdown menus position with `strategy: fixed` everywhere. The framed shell
// puts page content inside a panel with overflow: hidden (for its rounded
// corners and to contain sticky headers), and a menu opened near the panel's
// bottom edge was clipped by it. Fixed positioning escapes ancestor clipping;
// RotaEntryCard already used this per-trigger for the same reason.
Dropdown.Default.popperConfig = { strategy: "fixed" };

// Custom styles
import "@/assets/styles/custom.scss";
// Global vue-select theme (consistent caret/menu/highlight across all dropdowns)
import "@/assets/styles/vue-select.css";

// Legacy dark-mode key cleanup; safe to keep indefinitely (idempotent).
if (typeof localStorage !== "undefined") {
    localStorage.removeItem("theme");
}

// Default Vue
import { createApp } from "vue";
import App from "./App.vue";
import router from "./router/router";

// Forkto singletons — each lives in its own module so consumers can import
// directly without going through `@/main` (which used to create cycles).
// The `featureFlagsInstance` side-effect import ensures the singleton is
// constructed during bootstrap (App.vue also imports it, but listing it
// here documents the dependency explicitly).
import { emitter } from "@/services/emitter";
import { API } from "@/services/apiInstance";
import "@/services/featureFlagsInstance";
import { hasFeatureFlag, setDevOverrideStore, Flags } from "./classes/FeatureFlags";
import { userIsRole } from "./services/AuthService";

// Toasts
import { ToastPlugin } from "@/services/toasts";

// Tawk Messenger
import TawkMessengerVue from "@tawk.to/tawk-messenger-vue-3";

// Import gtag
import { createGtag } from "vue-gtag";

// Sentry
import * as Sentry from "@sentry/vue";

// Product analytics
import posthog from "posthog-js";
import { posthogLogger } from "@/services/posthogLogger";
import { isCanceledAPIError } from "@/services/apiErrors";

// Cookies
import VueCookies from "vue3-cookies";

// Vuex Store
import { store, initializeStoreSideEffects } from "./store";

// Vue Query
import { VueQueryPlugin, queryClient } from "./plugins/vueQuery";

setDevOverrideStore(store);
store.commit("restoreDevOverrides");

// Register API-dependent watchers now that the singleton exists.
initializeStoreSideEffects(API);

// Setup axios interceptors for automatic token refresh
import { setupAxiosInterceptors } from "./services/axiosInterceptor";
import { installAppUpdateRecovery } from "./services/appUpdateRecovery";
import {
    filterPreloadRecoverySentryEvent,
    installPreloadErrorRecovery
} from "./services/preloadErrorRecovery";
import { thirdPartyScriptsEnabled } from "./services/thirdPartyScripts";
setupAxiosInterceptors(API.Auth);

const app = createApp(App);

const posthogProjectToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
const posthogHost = import.meta.env.VITE_POSTHOG_HOST;
const posthogEnabled = Boolean(posthogProjectToken && posthogHost);
const isLocalHost = () => ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);

if (posthogEnabled) {
    posthog.init(posthogProjectToken, {
        api_host: posthogHost,
        // A single-page app: record a pageview on every router navigation, not
        // only the first load, so first-session paths are visible.
        capture_pageview: "history_change",
        // Error tracking: uncaught errors are captured automatically. Promise
        // rejections are captured by the unhandledrejection handler below, which
        // skips the API errors each view already reports to the user.
        capture_exceptions: {
            capture_unhandled_errors: true,
            capture_unhandled_rejections: false,
            capture_console_errors: false
        },
        // Match Sentry's replay privacy: never record typed input or page text.
        // The app shows supplier pricing, HACCP/allergen data and contact details.
        session_recording: {
            maskAllInputs: true,
            maskTextSelector: "*"
        },
        before_send: [
            // Like Sentry, keep local development errors out of production tracking.
            (event) => (event?.event === "$exception" && isLocalHost() ? null : event),
            // Login links once put passwords in the URL; never let one leave.
            redactCredentialsDeep
        ],
        logs: {
            serviceName: "forkto-web",
            environment: import.meta.env.MODE,
            serviceVersion: import.meta.env.VITE_VERSION
        }
    });
    posthog.register({ app_version: import.meta.env.VITE_VERSION, build_id: import.meta.env.VITE_BUILD_ID });
} else if (import.meta.env.DEV) {
    if (!posthogProjectToken) {
        console.error(
            new Error(
                "VITE_POSTHOG_PROJECT_TOKEN variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once VITE_POSTHOG_PROJECT_TOKEN is configured"
            )
        );
    }
    if (!posthogHost) {
        console.error(
            new Error(
                "VITE_POSTHOG_HOST variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once VITE_POSTHOG_HOST is configured"
            )
        );
    }
}

let lastUnexpectedErrorToastAt = 0;

function showUnexpectedErrorToast() {
    const now = Date.now();
    if (now - lastUnexpectedErrorToastAt < 5000) return;
    lastUnexpectedErrorToastAt = now;
    store.state.$toast?.error("Something went wrong. Please try again.");
}

// Initialize Sentry before mounting
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
installPreloadErrorRecovery({
    addBreadcrumb: sentryDsn ? Sentry.addBreadcrumb : undefined,
    captureException: sentryDsn ? Sentry.captureException : undefined,
    captureMessage: sentryDsn ? Sentry.captureMessage : undefined
});
installAppUpdateRecovery({
    addBreadcrumb: sentryDsn ? Sentry.addBreadcrumb : undefined
});

if (sentryDsn) {
    Sentry.init({
        app,
        dsn: sentryDsn,
        integrations: [
            Sentry.browserTracingIntegration({ router }),
            Sentry.replayIntegration({
                // F9: never record typed input or text content. This app handles
                // supplier pricing, HACCP/allergen data, contact details, and QA
                // notes — replay still captures layout and interactions, masked.
                maskAllText: true,
                maskAllInputs: true,
                blockAllMedia: true
            })
        ],
        environment: import.meta.env.MODE,
        sendDefaultPii: true,
        // Capture 20% of transactions for performance monitoring in production
        tracesSampleRate: import.meta.env.MODE === "production" ? 0.2 : 1.0,
        // Capture 10% of sessions for replay, 100% of sessions with errors
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        // Login links once put passwords in the URL; the page-load span keeps
        // the original URL, so redact every payload, not only errors.
        beforeSendTransaction: (event) => redactCredentialsDeep(event),
        beforeBreadcrumb: (breadcrumb) => redactCredentialsDeep(breadcrumb),
        // Don't send errors from local development
        beforeSend(event, hint) {
            if (isCanceledAPIError(hint?.originalException)) return null;
            if (window.location.hostname === "localhost") {
                return null;
            }
            if (!filterPreloadRecoverySentryEvent(event, hint)) {
                return null;
            }
            const frames = event.exception?.values?.[0]?.stacktrace?.frames;
            // Drop errors originating in third-party widgets we don't control.
            const thirdPartyHosts = ["clarity.js", "tawk.to"];
            if (frames?.some((f) => thirdPartyHosts.some((h) => f.filename?.includes(h)))) {
                return null;
            }
            // F9: sendDefaultPii attaches request headers/cookies — scrub the
            // credentials before the event leaves the browser.
            if (event.request?.headers) {
                delete event.request.headers.Authorization;
                delete event.request.headers.authorization;
                delete event.request.headers.Cookie;
                delete event.request.headers.cookie;
            }
            if (event.request) {
                delete event.request.cookies;
            }
            return redactCredentialsDeep(event);
        }
    });
}

app.config.errorHandler = (err, _instance, info) => {
    if (posthogEnabled) {
        posthog.captureException(err, { source: "vue_error_handler", vue_info: info });
        posthogLogger.error("vue application error handled", {
            source: "vue_error_handler",
            error_name: err?.name || "Error"
        });
    }
    if (sentryDsn) {
        Sentry.captureException(err, { contexts: { vue: { info } } });
    }
    showUnexpectedErrorToast();
};

if (typeof window !== "undefined") {
    window.addEventListener("unhandledrejection", (event) => {
        if (isCanceledAPIError(event.reason)) return;
        if (sentryDsn) {
            Sentry.captureException(event.reason);
        }
        // F26: API errors are expected and owned by the view that made the call
        // (its onError shows a contextual toast). Don't stack a generic
        // "something went wrong" on top — only toast for genuinely unexpected
        // rejections. Sentry still captures everything above.
        const reason = event.reason;
        const isNormalizedApiError =
            reason &&
            typeof reason === "object" &&
            "raw" in reason &&
            typeof reason.status === "number" &&
            typeof reason.code === "string";
        if (reason?.isApiError || isNormalizedApiError) {
            return;
        }
        if (posthogEnabled) {
            posthog.captureException(reason, { source: "window_unhandledrejection" });
        }
        posthogLogger.error("unhandled rejection handled", {
            source: "window_unhandledrejection",
            error_name: reason?.name || typeof reason
        });
        showUnexpectedErrorToast();
    });
}

function devMode() {
    return store.state.devOverrides.active;
}

import ForkModal from "./components/common/ForkModal.vue";
import FloatingInput from "./components/common/input/FloatingInput.vue";
import Notice from "./components/common/Notice.vue";

app.config.globalProperties.emitter = emitter;
app.config.globalProperties.devMode = devMode;
app.config.globalProperties.userIsRole = userIsRole;
app.config.globalProperties.Flags = Flags;
app.config.globalProperties.hasFeatureFlag = hasFeatureFlag;

app.use(router)
    .use(store)
    .use(VueQueryPlugin, { queryClient })
    .use(ToastPlugin, store.state.$toast)
    .use(VueCookies);

if (thirdPartyScriptsEnabled) {
    app.use(TawkMessengerVue, {
        propertyId: "63fd1fd231ebfa0fe7efaaf4",
        widgetId: "1gqac8jh3"
    }).use(
        createGtag({
            tagId: "G-9M4PD6BVFN"
        })
    );
} else if (typeof window !== "undefined") {
    window.Tawk_API = window.Tawk_API || { toggle() {} };
}

app.mount("#app");
posthogLogger.info("application mounted", { runtime: "browser" });

// Forkto components
app.component("ForkModal", ForkModal);
app.component("FloatingInput", FloatingInput);
// eslint-disable-next-line vue/multi-word-component-names
app.component("Notice", Notice);

import { nextTick } from "vue";

nextTick(() => {
    const spinner = document.getElementById("loadingSpinner");
    if (spinner) {
        spinner.style.display = "none";
    }
});
